import React, { type ReactNode, useLayoutEffect, useRef } from 'react';

import styled from 'styled-components';

import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import Tokens from '@grnhse/seedling/lib/birch/tokens';

import { withLocalePrefix } from 'shared/utils/translation';
import { UserMessage } from './user_message';
import { AssistantMessage } from './assistant_message';
import { Shimmer } from './shimmer';

const t = withLocalePrefix('shared.agent_chat');

export type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
};

type Labels = {
  loadEarlier: string;
  loadingEarlier: string;
  thinking: string;
};

type Props<M extends ChatMessage> = {
  messages: M[];
  streamingContent: string;
  isStreaming: boolean;
  hasMoreMessages?: boolean;
  loadingEarlier?: boolean;
  onLoadEarlier?: () => void;
  children?: ReactNode;
  labels?: Partial<Labels>;
  scrollToBottomKey?: unknown;
  // Already humanized by the feature; this component does not know what a tool is.
  statusText?: string | null;
  renderAssistantFooter?: (message: M) => ReactNode;
  // A feature's own row (e.g. a Step List); return undefined for the default bubble.
  renderMessage?: (message: M) => ReactNode | undefined;
  showStatus?: boolean;
  // A feature's own status (e.g. a Step List header) in place of the shimmer line.
  renderStatus?: (label: string) => ReactNode;
};

export function MessageList<M extends ChatMessage>({
  messages,
  streamingContent,
  isStreaming,
  hasMoreMessages,
  loadingEarlier,
  onLoadEarlier,
  children,
  labels,
  scrollToBottomKey,
  statusText,
  renderAssistantFooter,
  renderMessage,
  showStatus = true,
  renderStatus,
}: Props<M>) {
  const statusLabel = statusText ?? labels?.thinking ?? t('thinking');
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const prevFirstIdRef = useRef<ChatMessage['id'] | null>(null);
  const prevLastIdRef = useRef<ChatMessage['id'] | null>(null);
  const prevScrollToBottomKeyRef = useRef<unknown>(undefined);
  const prevScrollHeightRef = useRef(0);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }

    const firstId = messages.length ? messages[0].id : null;
    const lastId = messages.length ? messages[messages.length - 1].id : null;
    // Prepend (Load earlier): the tail is unchanged but the head grew. Keep the user's view
    // anchored by pushing scrollTop down by exactly the height added above, so the message they
    // were reading stays put instead of jumping.
    const isPrepend =
      lastId === prevLastIdRef.current && firstId !== prevFirstIdRef.current;
    const scrollToBottomKeyChanged =
      scrollToBottomKey !== prevScrollToBottomKeyRef.current;

    if (isPrepend) {
      el.scrollTop += el.scrollHeight - prevScrollHeightRef.current;
    } else if (
      isStreaming ||
      lastId !== prevLastIdRef.current ||
      scrollToBottomKeyChanged
    ) {
      // Append or streaming: follow the latest message to the bottom.
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }

    prevScrollHeightRef.current = el.scrollHeight;
    prevFirstIdRef.current = firstId;
    prevLastIdRef.current = lastId;
    prevScrollToBottomKeyRef.current = scrollToBottomKey;
  }, [messages, streamingContent, isStreaming, scrollToBottomKey, statusText]);

  return (
    <Container ref={containerRef} role="log">
      {hasMoreMessages && (
        <LoadEarlierRow>
          <TertiaryButton
            type="button"
            onClick={onLoadEarlier}
            disabled={loadingEarlier || isStreaming}
            size="sm"
          >
            {loadingEarlier
              ? labels?.loadingEarlier ?? t('loading_earlier')
              : labels?.loadEarlier ?? t('load_earlier')}
          </TertiaryButton>
        </LoadEarlierRow>
      )}

      {messages.map((message) => {
        const custom = renderMessage?.(message);
        if (custom !== undefined) {
          return <React.Fragment key={message.id}>{custom}</React.Fragment>;
        }
        return message.role === 'user' ? (
          <UserMessage key={message.id} content={message.content} />
        ) : (
          <AssistantMessage
            key={message.id}
            content={message.content}
            animate={false}
            footer={renderAssistantFooter?.(message)}
          />
        );
      })}

      {/* A live region would re-read the whole partial reply on every token; the finished
          message below announces once. */}
      {isStreaming && streamingContent && (
        <div aria-live="off">
          <AssistantMessage content={streamingContent} />
        </div>
      )}

      {/* A status survives below a partial answer: the agent often speaks first and then
          keeps looking things up, and that stretch needs a progress line too. */}
      {isStreaming && showStatus && (statusText || !streamingContent) && (
        <>{renderStatus?.(statusLabel) ?? <Shimmer>{statusLabel}</Shimmer>}</>
      )}

      {children}

      <div ref={bottomRef} />
    </Container>
  );
}

const LoadEarlierRow = styled.div`
  display: flex;
  justify-content: center;
  padding: ${Tokens.spacing['1']};
`;

const Container = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${Tokens.spacing['3']};
  padding-top: ${Tokens.spacing['1.5']};
  display: flex;
  flex-direction: column;
  gap: ${Tokens.spacing['3']};
`;
